// src/components/IntelHub/IntelligenceHubView.jsx
// Intelligence Hub — dual-persona data platform: business KPIs + technical pipeline ops
import React, { useState } from 'react';
import {
  Brain, Database, Zap, Cloud, Layers, BarChart2,
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Eye,
  Clock, Bell, Mail, MessageSquare, Globe, Server,
  Activity, ArrowRight, Users, Shield, FileText,
  ChevronDown, ChevronRight, Play, RefreshCw,
} from 'lucide-react';

// ─── Flowing-dot animation injected once ─────────────────────────────────────
const FLOW_CSS = `
@keyframes fl-move {
  0%   { left: 0px;  opacity: 0; }
  12%  { opacity: 1; }
  88%  { opacity: 1; }
  100% { left: 28px; opacity: 0; }
}
.fl-dot { position:absolute; top:50%; width:5px; height:5px; border-radius:50%;
  transform:translateY(-50%); animation: fl-move 1.7s linear infinite; }
.fl-dot.d1 { animation-delay:0s; }
.fl-dot.d2 { animation-delay:0.57s; }
.fl-dot.d3 { animation-delay:1.13s; }
`;

// ─── Data constants ───────────────────────────────────────────────────────────
const PIPELINE = [
  { id:'sources',   label:'Data Sources',    icon:Database, color:'#60a5fa', stat:'6 sources',  items:['PostgreSQL','Salesforce','Stripe','MongoDB'] },
  { id:'etl',       label:'ETL / ELT',       icon:Zap,      color:'#fbbf24', stat:'14 jobs',    items:['Airflow','Fivetran','dbt','AWS Glue'] },
  { id:'lake',      label:'Data Lake',       icon:Cloud,    color:'#34d399', stat:'8.4 TB',     items:['Amazon S3','Azure ADLS','Delta Lake'] },
  { id:'warehouse', label:'Warehouse',       icon:BarChart2,color:'#c084fc', stat:'234 tables', items:['Snowflake','BigQuery','Redshift'] },
  { id:'semantic',  label:'Semantic Layer',  icon:Layers,   color:'#f97316', stat:'86 metrics', items:['dbt Metrics','LookML','Cube.js'] },
  { id:'intel',     label:'Intelligence',    icon:Brain,    color:'#f43f5e', stat:'4 channels', items:['Dashboards','ML Insights','Alerts','Reports'] },
];

const SOURCES = [
  { id:'pg',  label:'PostgreSQL',     type:'Database',   color:'#4f86c6', icon:'🐘', fresh:'2m',  status:'healthy', records:'18.4M', tables:47 },
  { id:'sf',  label:'Salesforce CRM', type:'CRM',        color:'#00a1e0', icon:'☁️', fresh:'15m', status:'healthy', records:'2.1M',  tables:12 },
  { id:'str', label:'Stripe',         type:'Payments',   color:'#7c6bef', icon:'💳', fresh:'1m',  status:'healthy', records:'4.8M',  tables:8  },
  { id:'mg',  label:'MongoDB Atlas',  type:'Database',   color:'#4ade80', icon:'🍃', fresh:'8m',  status:'warning', records:'6.2M',  tables:23 },
  { id:'cw',  label:'AWS CloudWatch', type:'Monitoring', color:'#f97316', icon:'🟠', fresh:'30s', status:'healthy', records:'∞',     tables:3  },
  { id:'ji',  label:'Jira',           type:'ITSM',       color:'#4473c4', icon:'🎫', fresh:'5m',  status:'healthy', records:'340K',  tables:6  },
];

const ETL_JOBS = [
  { id:'j1', name:'salesforce-revenue-sync',  tool:'Fivetran',   freq:'15m',   status:'running', last:'4m',  rate:99.2, rows:'24K'  },
  { id:'j2', name:'stripe-transactions-etl',  tool:'Airflow',    freq:'1m',    status:'success', last:'1m',  rate:100,  rows:'8.4K' },
  { id:'j3', name:'postgres-user-events-elt', tool:'dbt',        freq:'1h',    status:'success', last:'22m', rate:98.1, rows:'180K' },
  { id:'j4', name:'support-tickets-sync',     tool:'Airbyte',    freq:'5m',    status:'success', last:'3m',  rate:97.4, rows:'1.2K' },
  { id:'j5', name:'mongodb-product-events',   tool:'Airflow',    freq:'10m',   status:'failed',  last:'8m',  rate:82.0, rows:'0'    },
  { id:'j6', name:'ml-churn-model-train',     tool:'SageMaker',  freq:'daily', status:'success', last:'4h',  rate:100,  rows:'—'    },
  { id:'j7', name:'dbt-core-transforms',      tool:'dbt',        freq:'30m',   status:'success', last:'12m', rate:99.8, rows:'2.1M' },
  { id:'j8', name:'cloudwatch-metrics-sink',  tool:'AWS Glue',   freq:'5m',    status:'running', last:'2m',  rate:99.9, rows:'420K' },
];

const KPIS = [
  { id:'rev',  label:'Monthly Revenue',     value:'$4.82M', delta:'+8.2%',       dir:'up',   color:'#34d399', source:'Stripe × Salesforce',  sub:'vs $4.46M last month',   spark:[42,45,43,47,44,48,46,49,48,51,52,54,53,55,57,56,58,60,59,62] },
  { id:'churn',label:'Churn Risk Score',    value:'4.1%',   delta:'+12% risk',   dir:'warn', color:'#f87171', source:'ML · churn-predictor-v3',sub:'847 at-risk accounts',   spark:[18,19,18,20,19,21,20,22,22,23,24,24,25,24,26,25,27,28,29,30] },
  { id:'sla',  label:'SLA Compliance',      value:'99.2%',  delta:'-0.3% target',dir:'warn', color:'#fbbf24', source:'CloudWatch × Jira',      sub:'Target: 99.5%',          spark:[99,99,99,100,99,100,99,100,100,99,99,100,99,99,99,99,100,99,99,99] },
  { id:'adop', label:'Feature Adoption',    value:'68.4%',  delta:'+15.2%',      dir:'up',   color:'#60a5fa', source:'Product events · MongoDB',sub:'Monthly active features', spark:[40,41,43,42,44,45,46,45,47,48,50,51,52,54,55,56,57,59,61,63] },
  { id:'nps',  label:'Net Promoter Score',  value:'47',     delta:'+3 pts',      dir:'up',   color:'#c084fc', source:'Survey platform',         sub:'Industry avg: 32',       spark:[36,37,36,38,38,39,40,40,41,41,42,43,43,44,44,45,45,46,46,47] },
  { id:'tick', label:'Open Support Tickets',value:'284',    delta:'+18% wk',     dir:'down', color:'#f97316', source:'Jira Service Desk',       sub:'P1:4 · P2:28 · P3:252',  spark:[180,185,190,188,195,200,198,205,210,215,218,222,228,235,240,250,258,265,275,284] },
];

const INSIGHTS = [
  {
    id:'i1', type:'Anomaly', sev:'HIGH', color:'#f87171', Icon:AlertTriangle,
    title:'Customer churn risk increased 12% this month',
    detail:'ML model detected behavioral shift in 847 accounts. Reduced login frequency (−34%), support tickets +18%, declining feature usage across core modules.',
    drivers:['Reduced login activity −34%','Support ticket volume +18%','Core feature usage declining'],
    confidence:91, model:'churn-predictor-v3',
    action:'Trigger retention playbook for at-risk segment',
  },
  {
    id:'i2', type:'Forecast', sev:'INFO', color:'#34d399', Icon:TrendingUp,
    title:'Revenue forecast: $5.1M next month (+6%)',
    detail:'Current pipeline velocity and expansion ARR suggest $280K incremental revenue. Q3 seasonality historically adds ~4% uplift to baseline.',
    drivers:['Pipeline velocity on track','Upsell opportunities: 124 accounts','Q3 seasonal uplift historical +4%'],
    confidence:84, model:'revenue-forecast-v2',
    action:'Review sales pipeline for 124 upsell opportunities',
  },
  {
    id:'i3', type:'Anomaly', sev:'MEDIUM', color:'#fbbf24', Icon:AlertTriangle,
    title:'SLA compliance trending below 99.5% target',
    detail:'P2 resolution time +22% over 7 days. Support team at 94% capacity; 3 critical bugs consuming engineering bandwidth.',
    drivers:['P2 resolution time +22%','Support capacity 94% utilized','3 open critical engineering bugs'],
    confidence:88, model:'sla-predictor-v1',
    action:'Escalate P2 backlog to engineering leads',
  },
  {
    id:'i4', type:'Opportunity', sev:'INFO', color:'#60a5fa', Icon:Eye,
    title:'3 high-ROI product optimization opportunities',
    detail:'Advanced Analytics activators retain 2.8× longer. Onboarding completion predicts 90d retention at 78% accuracy. API users have 3.4× higher NPS.',
    drivers:['Analytics activation → 2.8× retention','Onboarding prediction accuracy 78%','API users: NPS 3.4× higher'],
    confidence:76, model:'product-intelligence-v1',
    action:'Prioritize onboarding optimization in Q3 roadmap',
  },
  {
    id:'i5', type:'Correlation', sev:'LOW', color:'#c084fc', Icon:BarChart2,
    title:'Support spikes correlate with MongoDB sync lag (r=0.83)',
    detail:'8-minute MongoDB ingestion delay at 03:00 UTC consistently precedes user-reported data discrepancies 6-12h later (r=0.83 over 90 days).',
    drivers:['Correlation coefficient r=0.83','MongoDB lag: avg 8min at 03:00 UTC','Ticket spike 6–12h after lag event'],
    confidence:83, model:'correlation-engine-v2',
    action:'Fix MongoDB sync reliability — linked to INC-2038',
  },
];

const CHANNELS = [
  { id:'exec', label:'Executive Dashboard', Icon:BarChart2,      color:'#60a5fa', freq:'Weekly · Mon 08:00', subs:12,  tool:'Tableau',         status:'active' },
  { id:'ops',  label:'Ops Alerts',          Icon:Bell,           color:'#f87171', freq:'Real-time',          subs:34,  tool:'Grafana + PagerDuty',status:'active'},
  { id:'email',label:'Email Digest',        Icon:Mail,           color:'#34d399', freq:'Daily · 09:00',      subs:89,  tool:'Sendgrid',         status:'active' },
  { id:'slack',label:'Slack #data-insights',Icon:MessageSquare,  color:'#c084fc', freq:'Real-time',          subs:156, tool:'Slack API',         status:'active' },
  { id:'api',  label:'REST API Consumers',  Icon:Globe,          color:'#fbbf24', freq:'On-demand',          subs:8,   tool:'DataAPI v2',        status:'active' },
];

const MARTS = [
  { name:'Customer 360',      desc:'Revenue, churn, NPS, support per account', tables:12, consumers:['Sales','CS'],       color:'#60a5fa', fresh:'15m' },
  { name:'Revenue Analytics', desc:'ARR, MRR, expansion, churn revenue',       tables:8,  consumers:['Finance','Exec'],    color:'#34d399', fresh:'1m'  },
  { name:'SLA & Operations',  desc:'Incident SLOs, MTTD, MTTR, ticket trends', tables:9,  consumers:['Ops','Engineering'], color:'#fbbf24', fresh:'30s' },
  { name:'Product Adoption',  desc:'Feature funnels, activation, retention',   tables:14, consumers:['Product','Growth'],  color:'#c084fc', fresh:'5m'  },
];

const FRESHNESS = [
  { table:'fact_revenue',         schema:'finance',   fresh:'1m',  status:'healthy', rows:'4.8M',  src:'Stripe'      },
  { table:'dim_customers',        schema:'core',      fresh:'2m',  status:'healthy', rows:'182K',  src:'Salesforce'  },
  { table:'fact_incidents',       schema:'ops',       fresh:'30s', status:'healthy', rows:'2.1M',  src:'CloudWatch'  },
  { table:'ml_churn_scores',      schema:'ml',        fresh:'4h',  status:'warning', rows:'182K',  src:'SageMaker'   },
  { table:'agg_sla_compliance',   schema:'reporting', fresh:'30m', status:'healthy', rows:'8,760', src:'dbt'         },
  { table:'fact_support_tickets', schema:'support',   fresh:'5m',  status:'healthy', rows:'340K',  src:'Jira'        },
  { table:'product_events',       schema:'analytics', fresh:'8m',  status:'warning', rows:'6.2M',  src:'MongoDB'     },
  { table:'dim_products',         schema:'core',      fresh:'1h',  status:'healthy', rows:'1,240', src:'MongoDB'     },
];

const WH = { engine:'Snowflake', storage:'2.4 TB', tables:234, schemas:8, dailyQps:'4,821', cacheHit:'98.2%', avgMs:340, costDay:'$48.20' };

// ─── Helpers ──────────────────────────────────────────────────────────────────
function sparkPath(vals, w = 80, h = 22) {
  const mn = Math.min(...vals), mx = Math.max(...vals), rng = (mx - mn) || 1;
  const pts = vals.map((v, i) => [
    ((i / (vals.length - 1)) * w).toFixed(1),
    (h - ((v - mn) / rng) * h * 0.8 - h * 0.1).toFixed(1),
  ]);
  return 'M ' + pts.map(p => p.join(',')).join(' L ');
}

// ─── Pipeline flow diagram ────────────────────────────────────────────────────
function PipelineFlow() {
  return (
    <>
      <style>{FLOW_CSS}</style>
      <div className="flex items-stretch overflow-x-auto" style={{ padding: '4px 0 8px' }}>
        {PIPELINE.map((stage, i) => {
          const Icon = stage.icon;
          return (
            <div key={stage.id} className="flex items-center shrink-0">
              {/* Stage card */}
              <div style={{
                width: 120, borderRadius: 12, padding: '10px 10px 8px',
                background: `${stage.color}0d`, border: `1px solid ${stage.color}30`,
                textAlign: 'center',
              }}>
                <Icon size={16} style={{ color: stage.color, margin: '0 auto 4px' }}/>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text)', marginBottom: 2, lineHeight: 1.3 }}>{stage.label}</div>
                <div style={{ fontSize: 9, fontWeight: 700, color: stage.color, marginBottom: 4 }}>{stage.stat}</div>
                {stage.items.slice(0, 2).map(item => (
                  <div key={item} style={{ fontSize: 8, color: 'var(--text-3)', lineHeight: 1.5 }}>{item}</div>
                ))}
              </div>
              {/* Animated connector */}
              {i < PIPELINE.length - 1 && (
                <div style={{ position: 'relative', width: 32, height: 20, flexShrink: 0 }}>
                  <div style={{ position:'absolute', top:'50%', left:0, right:0, height:1,
                    background: `linear-gradient(90deg,${stage.color}60,${PIPELINE[i+1].color}60)`,
                    transform:'translateY(-50%)' }}/>
                  {['d1','d2','d3'].map(cls => (
                    <div key={cls} className={`fl-dot ${cls}`}
                      style={{ background: stage.color }}/>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

// ─── KPI card ─────────────────────────────────────────────────────────────────
function KPICard({ kpi }) {
  const path = sparkPath(kpi.spark);
  const isWarn = kpi.dir === 'warn';
  const isDown = kpi.dir === 'down';
  const arrowColor = kpi.dir === 'up' ? '#34d399' : isDown ? '#f87171' : '#fbbf24';
  return (
    <div className="rounded-xl p-3.5 flex flex-col gap-1" style={{ background: 'var(--bg-tertiary)', border: `1px solid ${kpi.color}20` }}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div style={{ fontSize: 9, color: 'var(--text-3)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.7px' }}>{kpi.label}</div>
          <div className="mono font-black" style={{ fontSize: 22, color: kpi.color, lineHeight: 1 }}>{kpi.value}</div>
        </div>
        {/* Sparkline */}
        <svg width={80} height={22} style={{ flexShrink: 0 }}>
          <path d={path} fill="none" stroke={kpi.color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" opacity={0.8}/>
          <path d={path + ` L80,22 L0,22 Z`} fill={kpi.color} opacity={0.08}/>
        </svg>
      </div>
      <div className="flex items-center gap-1.5">
        {kpi.dir === 'up' ? <TrendingUp size={10} style={{ color: arrowColor }}/> :
         kpi.dir === 'down' ? <TrendingDown size={10} style={{ color: arrowColor }}/> :
         <AlertTriangle size={10} style={{ color: arrowColor }}/>}
        <span style={{ fontSize: 10, fontWeight: 700, color: arrowColor }}>{kpi.delta}</span>
        <span style={{ fontSize: 9, color: 'var(--text-3)', marginLeft: 2 }}>{kpi.sub}</span>
      </div>
      <div style={{ fontSize: 8, color: 'var(--text-3)', marginTop: 1 }}>📡 {kpi.source}</div>
    </div>
  );
}

// ─── AI insight card ──────────────────────────────────────────────────────────
function InsightCard({ ins }) {
  const [open, setOpen] = useState(false);
  const { Icon } = ins;
  const ringCirc = 2 * Math.PI * 14;
  return (
    <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${ins.color}22`, background: `${ins.color}05` }}>
      <button className="w-full text-left px-4 py-3 flex items-start gap-3"
        style={{ background: 'none', border: 'none', cursor: 'pointer' }}
        onClick={() => setOpen(o => !o)}>
        <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
          style={{ background: `${ins.color}18`, border: `1px solid ${ins.color}30` }}>
          <Icon size={13} style={{ color: ins.color }}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <span style={{ fontSize: 10, fontWeight: 700, padding:'1px 6px', borderRadius:4, background:`${ins.color}18`, color: ins.color }}>{ins.type}</span>
            <span style={{ fontSize: 10, fontWeight: 700, padding:'1px 6px', borderRadius:4, background:'var(--bg-tertiary)', color:'var(--text-3)' }}>{ins.sev}</span>
          </div>
          <p className="text-xs font-semibold leading-snug" style={{ color:'var(--text)' }}>{ins.title}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Confidence ring */}
          <svg width={32} height={32}>
            <circle cx={16} cy={16} r={14} fill="none" stroke="var(--border)" strokeWidth={3}/>
            <circle cx={16} cy={16} r={14} fill="none" stroke={ins.color} strokeWidth={3}
              strokeDasharray={`${(ins.confidence/100)*ringCirc} ${ringCirc}`}
              strokeLinecap="round" transform="rotate(-90 16 16)"/>
            <text x={16} y={16} textAnchor="middle" dominantBaseline="middle"
              fontSize={8} fontWeight={700} fill={ins.color} fontFamily="Space Mono,monospace">{ins.confidence}%</text>
          </svg>
          {open ? <ChevronDown size={12} style={{ color:'var(--text-3)' }}/> : <ChevronRight size={12} style={{ color:'var(--text-3)' }}/>}
        </div>
      </button>
      {open && (
        <div className="px-4 pb-3 flex flex-col gap-2" style={{ borderTop: `1px solid ${ins.color}15` }}>
          <p className="text-xs leading-relaxed pt-2" style={{ color:'var(--text-2)' }}>{ins.detail}</p>
          <div>
            <div style={{ fontSize:9, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.7px', marginBottom:4 }}>Primary Drivers</div>
            {ins.drivers.map(d => (
              <div key={d} className="flex items-start gap-2 text-xs mb-1" style={{ color:'var(--text-2)' }}>
                <span style={{ color: ins.color, flexShrink:0 }}>▸</span>{d}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg mt-1"
            style={{ background:'rgba(34,197,94,.07)', border:'1px solid rgba(34,197,94,.2)', color:'#4ade80' }}>
            <CheckCircle size={11}/> <span><span style={{ fontWeight:700 }}>Recommended: </span>{ins.action}</span>
          </div>
          <div style={{ fontSize:9, color:'var(--text-3)' }}>Model: {ins.model}</div>
        </div>
      )}
    </div>
  );
}

// ─── Business view ────────────────────────────────────────────────────────────
function BusinessView() {
  return (
    <div className="flex flex-col gap-6">
      {/* KPI Grid */}
      <section>
        <div className="text-xs font-semibold mb-3 flex items-center gap-2"
          style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.8px' }}>
          <BarChart2 size={11}/> Business KPIs — curated from warehouse
        </div>
        <div className="grid grid-cols-3 gap-3">
          {KPIS.map(k => <KPICard key={k.id} kpi={k}/>)}
        </div>
      </section>

      {/* Two-column: Insights + Delivery */}
      <div className="grid grid-cols-5 gap-5">
        {/* AI Insights */}
        <section className="col-span-3 flex flex-col gap-2">
          <div className="text-xs font-semibold mb-1 flex items-center gap-2"
            style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.8px' }}>
            <Brain size={11} style={{ color:'#c084fc' }}/> AI-Generated Insights
            <span className="ml-auto px-1.5 py-0.5 rounded" style={{ fontSize:9, background:'rgba(192,132,252,.15)', color:'#c084fc', fontWeight:700 }}>
              {INSIGHTS.length} active
            </span>
          </div>
          {INSIGHTS.map(i => <InsightCard key={i.id} ins={i}/>)}
        </section>

        {/* Delivery channels + Data marts */}
        <section className="col-span-2 flex flex-col gap-4">
          {/* Delivery */}
          <div>
            <div className="text-xs font-semibold mb-2 flex items-center gap-2"
              style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.8px' }}>
              <Bell size={11}/> Delivery Channels
            </div>
            <div className="flex flex-col gap-2">
              {CHANNELS.map(ch => {
                const { Icon } = ch;
                return (
                  <div key={ch.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl"
                    style={{ background:'var(--bg-tertiary)', border:`1px solid ${ch.color}18` }}>
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                      style={{ background:`${ch.color}15`, border:`1px solid ${ch.color}25` }}>
                      <Icon size={12} style={{ color: ch.color }}/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold truncate">{ch.label}</div>
                      <div style={{ fontSize:9, color:'var(--text-3)' }}>{ch.freq} · {ch.tool}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="mono font-bold text-xs" style={{ color: ch.color }}>{ch.subs}</div>
                      <div style={{ fontSize:8, color:'var(--text-3)' }}>subs</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Data Mart catalog */}
          <div>
            <div className="text-xs font-semibold mb-2 flex items-center gap-2"
              style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.8px' }}>
              <FileText size={11}/> Data Mart Catalog
            </div>
            <div className="flex flex-col gap-2">
              {MARTS.map(m => (
                <div key={m.name} className="rounded-lg px-3 py-2"
                  style={{ background:'var(--bg-tertiary)', border:`1px solid ${m.color}18` }}>
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-xs font-semibold" style={{ color: m.color }}>{m.name}</span>
                    <span style={{ fontSize:8, color:'var(--text-3)' }}>↻ {m.fresh}</span>
                  </div>
                  <p style={{ fontSize:9, color:'var(--text-3)', marginBottom:3 }}>{m.desc}</p>
                  <div className="flex gap-1">
                    {m.consumers.map(c => (
                      <span key={c} style={{ fontSize:8, padding:'1px 5px', borderRadius:3, background:`${m.color}15`, color:m.color, fontWeight:700 }}>{c}</span>
                    ))}
                    <span style={{ fontSize:8, color:'var(--text-3)', marginLeft:2 }}>{m.tables} tables</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

// ─── Technical view ───────────────────────────────────────────────────────────
function TechnicalView() {
  return (
    <div className="flex flex-col gap-6">
      {/* Sources + Warehouse stats */}
      <div className="grid grid-cols-2 gap-5">
        {/* Sources */}
        <section>
          <div className="text-xs font-semibold mb-3 flex items-center gap-2"
            style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.8px' }}>
            <Database size={11}/> Data Sources
          </div>
          <div className="grid grid-cols-2 gap-2">
            {SOURCES.map(s => (
              <div key={s.id} className="rounded-xl p-3"
                style={{ background:'var(--bg-tertiary)', border:`1px solid ${s.status==='warning' ? '#f59e0b30' : s.color+'20'}` }}>
                <div className="flex items-center gap-2 mb-1.5">
                  <span style={{ fontSize:15 }}>{s.icon}</span>
                  <div>
                    <div className="text-xs font-semibold" style={{ lineHeight:1.2 }}>{s.label}</div>
                    <div style={{ fontSize:8, color:'var(--text-3)' }}>{s.type}</div>
                  </div>
                  <div className="ml-auto">
                    {s.status === 'healthy'
                      ? <CheckCircle size={11} style={{ color:'#22c55e' }}/>
                      : <AlertTriangle size={11} style={{ color:'#f59e0b' }}/>}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span style={{ fontSize:9, color:'var(--text-3)' }}>{s.records} records</span>
                  <span style={{ fontSize:9, fontWeight:700, color: s.status==='healthy' ? '#22c55e' : '#f59e0b' }}>↻ {s.fresh}</span>
                </div>
                <div style={{ fontSize:8, color:'var(--text-3)', marginTop:2 }}>{s.tables} tables</div>
              </div>
            ))}
          </div>
        </section>

        {/* Warehouse stats */}
        <section>
          <div className="text-xs font-semibold mb-3 flex items-center gap-2"
            style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.8px' }}>
            <BarChart2 size={11}/> Warehouse · {WH.engine}
          </div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            {[
              { l:'Storage',       v: WH.storage,    c:'#60a5fa' },
              { l:'Tables',        v: WH.tables,     c:'#c084fc' },
              { l:'Daily Queries', v: WH.dailyQps,   c:'#34d399' },
              { l:'Cache Hit',     v: WH.cacheHit,   c:'#fbbf24' },
              { l:'Avg Query',     v:`${WH.avgMs}ms`,c:'#f97316' },
              { l:'Cost / Day',    v: WH.costDay,    c:'#94a3b8' },
            ].map(s => (
              <div key={s.l} className="rounded-lg p-2.5 text-center"
                style={{ background:'var(--bg-tertiary)', border:`1px solid ${s.c}20` }}>
                <div className="mono font-black text-base" style={{ color: s.c }}>{s.v}</div>
                <div style={{ fontSize:8, color:'var(--text-3)' }}>{s.l}</div>
              </div>
            ))}
          </div>
          {/* Schemas chip list */}
          <div style={{ fontSize:9, color:'var(--text-3)', marginBottom:4 }}>SCHEMAS ({WH.schemas})</div>
          <div className="flex flex-wrap gap-1">
            {['finance','core','ops','ml','reporting','support','analytics','staging'].map(s => (
              <span key={s} style={{ fontSize:9, padding:'1px 7px', borderRadius:4, background:'var(--bg-primary)', color:'var(--text-3)', border:'1px solid var(--border)' }}>{s}</span>
            ))}
          </div>
        </section>
      </div>

      {/* ETL Jobs */}
      <section>
        <div className="text-xs font-semibold mb-3 flex items-center gap-2"
          style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.8px' }}>
          <Zap size={11}/> ETL / ELT Jobs
          <span className="ml-auto px-1.5 py-0.5 rounded" style={{ fontSize:9, background:'rgba(239,68,68,.12)', color:'#f87171', fontWeight:700 }}>
            {ETL_JOBS.filter(j=>j.status==='failed').length} failed
          </span>
        </div>
        <div className="rounded-xl overflow-hidden" style={{ border:'1px solid var(--border)' }}>
          {/* Header */}
          <div className="grid px-4 py-2" style={{ gridTemplateColumns:'2fr 90px 55px 65px 55px 60px', background:'var(--bg-tertiary)', borderBottom:'1px solid var(--border)' }}>
            {['Job Name','Tool','Freq','Status','Last Run','Success'].map(h => (
              <span key={h} style={{ fontSize:9, fontWeight:700, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.6px' }}>{h}</span>
            ))}
          </div>
          {ETL_JOBS.map((job, i) => {
            const sc = job.status==='running' ? '#60a5fa' : job.status==='success' ? '#22c55e' : '#ef4444';
            const si = job.status==='running' ? <RefreshCw size={10} style={{ color:sc }}/> :
                       job.status==='success' ? <CheckCircle size={10} style={{ color:sc }}/> :
                       <AlertTriangle size={10} style={{ color:sc }}/>;
            return (
              <div key={job.id} className="grid px-4 py-2.5 items-center"
                style={{ gridTemplateColumns:'2fr 90px 55px 65px 55px 60px',
                  borderBottom: i < ETL_JOBS.length-1 ? '1px solid rgba(255,255,255,.03)' : 'none',
                  background: job.status==='failed' ? 'rgba(239,68,68,.04)' : 'transparent' }}>
                <span className="mono text-xs truncate" style={{ color:'var(--text)' }}>{job.name}</span>
                <span style={{ fontSize:9, padding:'1px 6px', borderRadius:3, background:'var(--bg-primary)', color:'var(--text-3)', border:'1px solid var(--border)', width:'fit-content' }}>{job.tool}</span>
                <span style={{ fontSize:9, color:'var(--text-3)' }}>{job.freq}</span>
                <span className="flex items-center gap-1" style={{ fontSize:10, color:sc, fontWeight:700 }}>{si}{job.status}</span>
                <span style={{ fontSize:9, color:'var(--text-3)' }}>{job.last} ago</span>
                <div>
                  <div style={{ fontSize:9, fontWeight:700, color: job.rate>98 ? '#22c55e' : job.rate>90 ? '#fbbf24' : '#ef4444' }}>{job.rate}%</div>
                  <div style={{ height:2, borderRadius:99, background:'var(--border)', marginTop:2 }}>
                    <div style={{ height:'100%', width:`${job.rate}%`, borderRadius:99, background: job.rate>98?'#22c55e':job.rate>90?'#fbbf24':'#ef4444' }}/>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Data freshness */}
      <section>
        <div className="text-xs font-semibold mb-3 flex items-center gap-2"
          style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.8px' }}>
          <Clock size={11}/> Data Freshness — Warehouse Tables
          <span className="ml-auto px-1.5 py-0.5 rounded" style={{ fontSize:9, background:'rgba(245,158,11,.12)', color:'#fbbf24', fontWeight:700 }}>
            {FRESHNESS.filter(t=>t.status==='warning').length} stale
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {FRESHNESS.map(t => (
            <div key={t.table} className="flex items-center gap-3 px-3 py-2.5 rounded-xl"
              style={{ background:'var(--bg-tertiary)', border:`1px solid ${t.status==='warning'?'#f59e0b30':'var(--border)'}` }}>
              <div style={{ width:6, height:6, borderRadius:'50%', background: t.status==='healthy'?'#22c55e':'#f59e0b', flexShrink:0 }}/>
              <div className="flex-1 min-w-0">
                <div className="mono text-xs font-semibold truncate">{t.table}</div>
                <div style={{ fontSize:8, color:'var(--text-3)' }}>{t.schema} · {t.rows} rows · {t.src}</div>
              </div>
              <div className="text-right shrink-0">
                <div style={{ fontSize:10, fontWeight:700, color: t.status==='warning'?'#f59e0b':'#22c55e' }}>{t.fresh}</div>
                <div style={{ fontSize:8, color:'var(--text-3)' }}>ago</div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────
export default function IntelligenceHubView() {
  const [persona, setPersona] = useState('business');
  const openJobs    = ETL_JOBS.filter(j => j.status !== 'failed').length;
  const failedJobs  = ETL_JOBS.filter(j => j.status === 'failed').length;
  const highInsights = INSIGHTS.filter(i => i.sev === 'HIGH').length;

  return (
    <div className="flex flex-col h-full">
      {/* ── Header ── */}
      <div className="px-5 py-4 shrink-0" style={{ background:'var(--bg-secondary)', borderBottom:'1px solid var(--border)' }}>
        <div className="flex items-center gap-4 flex-wrap">
          {/* Icon + title */}
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ background:'linear-gradient(135deg,#60a5fa,#c084fc)', boxShadow:'0 4px 14px rgba(96,165,250,.35)' }}>
            <Brain size={18} className="text-white"/>
          </div>
          <div>
            <h1 className="text-base font-bold">Intelligence Hub</h1>
            <p className="text-xs mt-0.5" style={{ color:'var(--text-3)' }}>
              Unified data platform · ETL pipeline monitoring · AI-powered business insights
            </p>
          </div>

          {/* Health chips */}
          <div className="flex items-center gap-2 flex-wrap ml-2">
            {[
              { l:'Sources',   v:`${SOURCES.filter(s=>s.status==='healthy').length}/${SOURCES.length}`, c:'#34d399' },
              { l:'ETL Jobs',  v:`${openJobs}/${ETL_JOBS.length}`,  c: failedJobs>0 ? '#f87171' : '#34d399' },
              { l:'Insights',  v:`${INSIGHTS.length} active`,        c:'#c084fc' },
              { l:'Warehouse', v:WH.engine,                           c:'#60a5fa' },
            ].map(chip => (
              <div key={chip.l} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg"
                style={{ background:`${chip.c}12`, border:`1px solid ${chip.c}25` }}>
                <span style={{ fontSize:9, color:'var(--text-3)' }}>{chip.l}</span>
                <span className="mono font-bold" style={{ fontSize:10, color:chip.c }}>{chip.v}</span>
              </div>
            ))}
          </div>

          {/* Persona toggle */}
          <div className="ml-auto flex items-center gap-1 p-0.5 rounded-xl" style={{ background:'var(--bg-primary)', border:'1px solid var(--border)' }}>
            {[
              { id:'business',  label:'👔 Business', desc:'KPIs & AI insights' },
              { id:'technical', label:'⚙ Technical', desc:'Pipeline & warehouse' },
            ].map(p => (
              <button key={p.id} onClick={() => setPersona(p.id)}
                className="px-4 py-1.5 rounded-lg text-xs font-semibold transition-all"
                style={{
                  background: persona===p.id ? 'linear-gradient(135deg,rgba(96,165,250,.2),rgba(192,132,252,.2))' : 'transparent',
                  color: persona===p.id ? 'var(--text)' : 'var(--text-3)',
                  border: persona===p.id ? '1px solid rgba(96,165,250,.3)' : '1px solid transparent',
                  cursor: 'pointer',
                }}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Scrollable body ── */}
      <div className="flex-1 overflow-y-auto">
        {/* Pipeline architecture — always visible */}
        <div className="px-5 pt-4 pb-2" style={{ borderBottom:'1px solid var(--border)', background:'var(--bg-secondary)' }}>
          <div className="flex items-center gap-2 mb-2">
            <div style={{ width:6, height:6, borderRadius:'50%', background:'#22c55e', animation:'pulse-anim 2s ease-in-out infinite' }}/>
            <span className="text-xs font-semibold" style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.8px' }}>
              Data Pipeline Architecture — live
            </span>
          </div>
          <PipelineFlow/>
        </div>

        {/* Context bar */}
        <div className="px-5 py-2 flex items-center gap-3" style={{ background:`linear-gradient(90deg,${persona==='business'?'rgba(96,165,250,.04)':'rgba(251,191,36,.04)'},transparent)`, borderBottom:'1px solid var(--border)' }}>
          {persona === 'business' ? (
            <>
              <Brain size={11} style={{ color:'#c084fc' }}/>
              <span className="text-xs" style={{ color:'var(--text-3)' }}>
                Business view — curated KPIs from warehouse · AI-powered insights · delivery channel status
              </span>
              {highInsights > 0 && (
                <span className="ml-auto px-2 py-0.5 rounded text-xs font-bold"
                  style={{ background:'rgba(248,113,113,.12)', color:'#f87171' }}>
                  {highInsights} high-priority insights require attention
                </span>
              )}
            </>
          ) : (
            <>
              <Zap size={11} style={{ color:'#fbbf24' }}/>
              <span className="text-xs" style={{ color:'var(--text-3)' }}>
                Technical view — source health · ETL job status · warehouse metrics · data freshness
              </span>
              {failedJobs > 0 && (
                <span className="ml-auto px-2 py-0.5 rounded text-xs font-bold"
                  style={{ background:'rgba(239,68,68,.12)', color:'#f87171' }}>
                  {failedJobs} job{failedJobs>1?'s':''} failed — requires investigation
                </span>
              )}
            </>
          )}
        </div>

        {/* View content */}
        <div className="p-5">
          {persona === 'business' ? <BusinessView/> : <TechnicalView/>}
        </div>
      </div>
    </div>
  );
}
